import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  IconButton,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import Header from "../../Components/Common/Header";
import SearchBox from "../../Components/Search/SearchBox";
import { useLocation } from "react-router-dom";
import { DoneSharp } from "@mui/icons-material";
import { colors } from "../../theme/theme";
import Footer from "../../Components/Common/Footer";
import axios from "axios";
import config from "../../config/config";
import { useNavigate } from "react-router-dom";
import SearchCardCopy from "../../Components/Search/SearchCardCopy";
import { motion } from "framer-motion";
import SearchCard from "../../Components/Search/SearchCard";
import { logUserAction } from "../../utils/analytics";
import SearchCategoryCard from "../../Components/Search/SearchCategoryCard";

const Search = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchCalledRef = React.useRef(false);

  const [search, setSearch] = useState(location.state?.search || "");
  const [searchResults, setSearchResults] = useState([]);

  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  const [categorySelect, setCategorySelect] = useState("handpik");
  const [categoryCollection, setCategoryCollection] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allResearchSteps, setAllResearchSteps] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [intervalComplete, setIntervalComplete] = useState(false);
  const [noResultAvailable, setNoResultAvailable] = useState(false);
  const [ipInfo, setIpInfo] = useState(null);
  const [searchQuery, setSearchQuery] = useState(location.state?.search || "");

  const [currentImageIndex, setCurrentImageIndex] = useState({});

  const [dots, setDots] = useState("");

  useEffect(() => {
    if (loading || !intervalComplete) {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length < 3 ? prev + "." : ""));
      }, 500);
      return () => clearInterval(interval);
    } else {
      setDots("");
    }
  }, [loading, intervalComplete]);

  const getPercentage = (salePercentage) => {
    return (
      Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(salePercentage * 100) + "%"
    );
  };

  const getSearch = async () => {
    if (search === "") return;
    setCategoryCollection([]);
    setSearchResults([]);
    getCollectionsByCategory();
    setNoResultAvailable(false);

    try {
      setSearchLoading(true);
      localStorage.setItem("query", search);
      const searchResult = await axios.get(
        `${config.backendUrl}/inventory/search?q=${search}`
      );

      if (searchResult.status === 200) {
        const categories = searchResult.data?.results || [];

        setSearchResults(categories);
        setNoResultAvailable(categories.length === 0);

        await logUserAction({
          action: "search",
          data: { keyword: search, searchResults: categories },
          timestamp: new Date().toISOString(),
        });

        await localStorage.setItem("searchResult", JSON.stringify(categories));

        return categories;
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
    } finally {
      setSearchLoading(false);
    }
    return [];
  };

  const getCollectionsByCategory = async () => {
    const searchResult = await axios.get(
      `${config.backendUrl}/inventory/list_of_categories?q=${search}`
    );
    setAllCategories(
      searchResult.data?.results.sort((a, b) =>
        a.name === "On Sale" ? -1 : b.name === "On Sale" ? 1 : 0
      )
    );
    await localStorage.setItem(
      "categoryCollection",
      JSON.stringify(searchResult.data?.results)
    );
    // const sortedCategories = categories.sort((a, b) => (a.name === "On Sale" ? -1 : b.name === "On Sale" ? 1 : 0));
    // console.log("response", searchResult.data?.collections);
  };

  const getResearchSteps = async () => {
    if (!search) return;

    localStorage.removeItem("query");
    localStorage.removeItem("searchResult");
    localStorage.removeItem("categoryCollection");
    localStorage.removeItem("researchSteps");

    searchCalledRef.current = true;

    setAllResearchSteps([]);
    setSearchResults([]);
    setCategoryCollection([]);
    setAllCategories([]);
    setIntervalComplete(false);
    setCurrentMessageIndex(0);
    setNoResultAvailable(false);
    setSearchQuery(search);

    try {
      setLoading(true);
      const researchStepsRes = await axios.get(
        `${config.backendUrl}/inventory/research_steps?q=${search}`
      );

      if (researchStepsRes.status === 200) {
        const steps = researchStepsRes.data.results.research_steps;
        setAllResearchSteps(steps);
        localStorage.setItem("researchSteps", JSON.stringify(steps));

        let index = 0;
        const interval = setInterval(() => {
          setCurrentMessageIndex(index);
          index++;
          if (index >= steps.length) {
            clearInterval(interval);
            setIntervalComplete(true);
          }
        }, 1000);
      }

      await getSearch();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    const query = await localStorage.getItem("query");
    const searchResult = await localStorage.getItem("searchResult");
    const categoryCollection = await localStorage.getItem("categoryCollection");
    const researchSteps = await localStorage.getItem("researchSteps");

    if (searchResult && query === search) {
      console.log("Found in localStorage");
      const parsedResults = JSON.parse(searchResult);
      setSearchResults(parsedResults);

      if (categoryCollection) {
        setAllCategories(JSON.parse(categoryCollection));
      }

      if (researchSteps) {
        const parsedSteps = JSON.parse(researchSteps);
        setAllResearchSteps(parsedSteps);
        setCurrentMessageIndex(parsedSteps.length - 1);
      }

      setIntervalComplete(true);
      if (parsedResults.length === 0) {
        setNoResultAvailable(true);
      }
    } else {
      console.log("Couldn't find in localStorage");
      getResearchSteps();
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (intervalComplete || noResultAvailable) {
      searchCalledRef.current = false;
    }
  }, [intervalComplete, noResultAvailable]);

  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const mediumScreen = useMediaQuery((theme) =>
    theme.breakpoints.between("sm", "lg")
  );

  return (
    <Box sx={{ backgroundColor: "#FEFEFE" }}>
      <Header />
      {/* Search Box */}
      <SearchBox
        search={search}
        setSearch={setSearch}
        startSearch={getResearchSteps}
        loading
      />

      <Box
        sx={{
          borderRadius: "20px",
          backgroundColor: "#fff",
          boxShadow: "-1px -2px 15px 0px #00000012",
          ml: smallScreen ? 2 : mediumScreen ? "50px" : "200px", // Mobile: 2, Tablet: 50px, Desktop: 200px
          py: 0.125,
          mt: 2,
          mr: smallScreen ? 0 : mediumScreen ? "50px" : "200px", // Mobile: 0, Tablet: 50px, Desktop: 200px
          overflowX: "visible",
          position: "relative",
        }}
      >
        {/* Loading Messages */}
        <Box className="m-4">
          <Box
            sx={{
              boxShadow: "0px 0px 5.5px 0px #00000026",
              backgroundColor: "#FFB79C",
              borderTopLeftRadius: "20px",
              borderBottomRightRadius: "20px",
              borderTopRightRadius: "20px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              py: 1.5,
              px: 2,
              mb: 2,
              width: "fit-content",
              minWidth: "fit-content",
              gap: 1,
              mt: 2,
            }}
          >
            <motion.img
              src={require("../../assets/icons/sparkles.png")}
              alt="sparkle"
              style={{ height: "20px", width: "20px" }}
              animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }}
              transition={{
                repeat: Infinity,
                duration: 1.2,
                ease: "easeInOut",
              }}
            />
            <Typography
              sx={{
                fontFamily: "Poppins-SemiBold",
                fontSize: "14.21px",
                fontWeight: "600",
                color: "#fff",
              }}
            >
              {loading || !intervalComplete
                ? `${searchQuery} finding${dots}`
                : `${searchQuery} results found!`}
            </Typography>
          </Box>
          <Box
            sx={{
              boxShadow: "0px 0px 5.5px 0px #00000026",
              backgroundColor: "#FFF",
              borderTopLeftRadius: "12px",
              borderBottomRightRadius: "12px",
              borderTopRightRadius: "12px",
              display: "flex",
              flexDirection: "column",
              p: 1.5,
              mb: 2,
              width: "327px",
              minWidth: "fit-content",
              gap: 1.25,
            }}
          >
            {(allResearchSteps || [])
              .slice(0, currentMessageIndex + 1)
              .map((msg, index) => (
                <Stack
                  key={index}
                  direction="row"
                  sx={{ alignItems: "center", gap: 1 }}
                >
                  {index === currentMessageIndex ? (
                    <img
                      src={require("../../assets/images/login-handpik-logo.png")}
                      style={{
                        height: "20px",
                        width: "20px",
                        borderRadius: "50%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderWidth: 2,
                        borderColor:
                          categorySelect === "handpik" ? "#FFB79C" : "#9AE0EB",
                      }}
                      alt="logo"
                    />
                  ) : (
                    <Box
                      sx={{
                        height: "20px",
                        width: "20px",
                        borderRadius: "50%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "#BBDEA5",
                      }}
                    >
                      <DoneSharp sx={{ fontSize: "10px" }} />
                    </Box>
                  )}

                  <Typography
                    className="text-black text-sm transition-opacity duration-500 ease-in-out animate-fade-in"
                    sx={{
                      fontFamily: "Poppins-SemiBold",
                      fontSize: "12px",
                    }}
                  >
                    {msg}
                  </Typography>
                </Stack>
              ))}
          </Box>
        </Box>

        {/* Brands Slider */}
        {intervalComplete && searchResults?.length !== 0 && (
          <>
            <Box
              sx={{
                height: "2px",
                backgroundColor: "#D9D9D9",
                mx: 2,
                my: 1,
              }}
            />
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                whiteSpace: "nowrap",
                my: 2,
                width: "100%",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <Box
                sx={{
                  px: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  ml: 2,
                  flexShrink: 0,
                  cursor: "pointer",
                }}
                onClick={() => {
                  setCategorySelect("handpik");
                  setCategoryCollection([]);
                }}
              >
                <img
                  src={require("../../assets/images/login-handpik-logo.png")}
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "360px",
                    borderWidth: categorySelect === "handpik" ? "2px" : "0px",
                    borderColor:
                      categorySelect === "handpik" ? "#FFB79C" : "transparent",
                    padding: "2px",
                  }}
                  alt="logo"
                />
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontFamily: "Poppins-Medium",
                    color: "#464646",
                    textAlign: "center",
                    mt: 0.5,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "60px",
                  }}
                >
                  HandPik.AI
                </Typography>
              </Box>

              {intervalComplete && allCategories?.length > 0
                ? allCategories.map(
                    (item, index) =>
                      item?.products.length > 0 && (
                        <Box
                          sx={{
                            px: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            ml: index === 0 ? 0 : 0,
                            flexShrink: 0,
                            cursor: "pointer",
                          }}
                          onClick={async () => {
                            setCategorySelect(item?.name);
                            setCategoryCollection(item?.products);

                            await logUserAction({
                              action: "category_click",
                              data: {
                                category: item?.name || null,
                                products: item?.products || [],
                              },
                              timestamp: new Date().toISOString(),
                            });
                          }}
                          key={index}
                        >
                          <img
                            src={item?.products[0]?.image_url}
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "360px",
                              borderWidth:
                                categorySelect === item?.name ? "2px" : "0px",
                              borderColor:
                                categorySelect === item?.name
                                  ? "#FFB79C"
                                  : "#9AE0EB",
                              padding: "2px",
                            }}
                            onError={(e) =>
                              (e.target.src = require("../../assets/images/no_image.png"))
                            }
                            alt="logo"
                          />
                          <Typography
                            sx={{
                              fontSize: "10px",
                              fontFamily: "Poppins-Medium",
                              color: "#464646",
                              textAlign: "center",
                              mt: 0.5,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              width: "60px",
                            }}
                          >
                            {item?.name}
                          </Typography>
                        </Box>
                      )
                  )
                : ""}
            </Box>
          </>
        )}

        {/* Card */}
        {intervalComplete &&
          categoryCollection.length === 0 &&
          searchResults?.length > 0 && (
            <SearchCard
              searchResults={searchResults}
              currency={ipInfo?.currency || "USD"}
              searchQuery={search}
            />
          )}

        {noResultAvailable && !searchLoading && (
          <Box
            sx={{
              borderRadius: "20px",
              backgroundColor: "#fff",
              boxShadow: "-1px -2px 15px 0px #00000012",
              p: 2,
              m: 2,
              overflowX: "visible",
              position: "relative",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Poppins-SemiBold",
                fontSize: "14.21px",
                fontWeight: "600",
                color: "#000",
              }}
            >
              Unfortunately, I couldn't find any relevant products meeting your
              requirements
            </Typography>
          </Box>
        )}

        {categoryCollection?.length > 0 && (
          <SearchCategoryCard
            categoryCollection={categoryCollection}
            search={search}
          />
        )}
      </Box>

      <Footer />
    </Box>
  );
};

export default Search;
